export type ContentType = "document" | "instructions" | "process"

export interface Favourite {
  id: number
  uuid: string
  content_id: number
  content_type: ContentType
  title: string
}

export type ProgressData = Record<string, 0 | 1>

export interface Progress {
  id: number
  uuid: string
  content_id: number
  progress: ProgressData
}

export interface APIError {
  err: string
}

export interface APIResponse<T> {
  data: T
  error?: string
}

export class QBuildAPI {
  private baseUrl: string
  private favouritesCache: APIResponse<Favourite[]> | null = null

  constructor(baseUrl: string = "https://qbuild.d8.net.au") {
    this.baseUrl = baseUrl
  }

  private async makeRequest<T>(
    endpoint: string,
    method: string,
    body?: unknown
  ): Promise<APIResponse<T>> {
    try {
      const options: RequestInit = {
        method,
        headers: {
          "Content-Type": "application/json",
        },
      }

      if (body) {
        options.body = JSON.stringify(body)
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, options)
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.err || "An error occurred")
      }

      return { data }
    } catch (error) {
      return {
        data: null as unknown as T,
        error: error instanceof Error ? error.message : "Unknown error",
      }
    }
  }

  async addFavourite(
    uuid: string,
    content_id: number,
    content_type: ContentType,
    title: string
  ): Promise<APIResponse<Favourite>> {
    this.favouritesCache = null // Invalidate cache
    return this.makeRequest<Favourite>(`/favourites/${uuid}`, "POST", {
      content_id,
      content_type,
      title,
    })
  }

  async listFavourites(uuid: string): Promise<APIResponse<Favourite[]>> {
    if (this.favouritesCache !== null) return this.favouritesCache
    const response = await this.makeRequest<Favourite[]>(
      `/favourites/${uuid}`,
      "GET"
    )
    this.favouritesCache = response
    return response
  }

  async listFavouritesByType(
    uuid: string,
    content_type: ContentType
  ): Promise<APIResponse<Favourite[]>> {
    const response = await this.listFavourites(uuid)
    if (response.error) return response
    return {
      ...response,
      data: response.data.filter((fav) => fav.content_type === content_type),
    }
  }

  async checkFavourite(
    uuid: string,
    content_id: number
  ): Promise<APIResponse<Favourite>> {
    return this.makeRequest<Favourite>(
      `/favourites/${uuid}/${content_id}`,
      "GET"
    )
  }

  async deleteFavourite(
    uuid: string,
    content_id: number
  ): Promise<APIResponse<{ message: string }>> {
    this.favouritesCache = null // Invalidate cache
    return this.makeRequest<{ message: string }>(
      `/favourites/${uuid}/${content_id}`,
      "DELETE"
    )
  }

  async setProgress(
    uuid: string,
    content_id: number,
    progress: ProgressData
  ): Promise<APIResponse<Progress>> {
    return this.makeRequest<Progress>(
      `/progress/${uuid}/${content_id}`,
      "POST",
      { progress }
    )
  }

  async getProgress(
    uuid: string,
    content_id: number
  ): Promise<APIResponse<Progress>> {
    return this.makeRequest<Progress>(`/progress/${uuid}/${content_id}`, "GET")
  }

  async deleteProgress(
    uuid: string,
    content_id: number
  ): Promise<APIResponse<{ message: string }>> {
    return this.makeRequest<{ message: string }>(
      `/progress/${uuid}/${content_id}`,
      "DELETE"
    )
  }
}

// Auto-create a default instance
export const api = new QBuildAPI()
